import { t } from '../i18n';
import { useRef, useState } from 'react';

import { ActionPopover } from './ActionPopover';
import { Glyph } from './icons';
import { CHIP_COLOR } from './database-rows';
import { OptionEditor } from './OptionEditor';
import type { ChipColor, PropertyOption } from '@noti/shared';

/**
 * The prototype's `dd` macro: a trigger button with a floating option list.
 * Used for property type, select/status/person cells, filter values, sort keys
 * and the group-by. Geometry comes from `dd__*` in workspace.css.
 */

export interface DropdownOption {
  value: string;
  label: string;
  chip?: ChipColor | null;
  icon?: string | null;
  /** Options the user may rename, recolour and delete in place. */
  editable?: boolean;
}

export interface DropdownProps {
  label: string;
  exclusive?: boolean;
  value: string;
  options: DropdownOption[];
  onPick: (value: string) => void;
  onCreate?: (name: string) => void;
  onRename?: (value: string, name: string) => void;
  onRecolor?: (value: string, color: ChipColor) => void;
  onRemove?: (value: string) => void;
  /** The prototype's `noChev`: a bare chip, no chevron. */
  noChevron?: boolean;
  /** Full-width trigger, used inside menus. */
  block?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export function Dropdown({
  label,
  exclusive = false,
  value,
  options,
  onPick,
  onCreate,
  onRename,
  onRecolor,
  onRemove,
  noChevron = false,
  block = false,
  className = '',
  children,
}: DropdownProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);
  const showSearch = Boolean(onCreate);

  const current = options.find((option) => option.value === value);
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? options.filter((option) => option.label.toLowerCase().includes(needle))
    : options;
  const exact = options.some((option) => option.label.toLowerCase() === needle);
  const canCreate = Boolean(onCreate) && needle !== '' && !exact;

  const changeOpen = (next: boolean) => {
    if (next) {
      setQuery('');
      setEditing(null);
    }
    setOpen(next);
  };

  const pick = (option: DropdownOption) => {
    onPick(option.value);
    setOpen(false);
    setQuery('');
  };

  // Options and the search field (which sits below them) form one vertical list for the arrows.
  // Keys typed in the option editor belong to its own fields.
  const moveFocus = (event: React.KeyboardEvent<HTMLElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const inSearch = target === searchRef.current;
    if (!inSearch && target.getAttribute('role') !== 'option') return;
    const optionStops = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="option"]')];
    const stops = searchRef.current ? [...optionStops, searchRef.current] : optionStops;
    const at = stops.indexOf(target);
    let next: HTMLElement | undefined;
    if (event.key === 'ArrowDown') next = stops[at + 1];
    else if (event.key === 'ArrowUp') next = stops[at - 1];
    else if (!inSearch && event.key === 'Home') next = optionStops[0];
    else if (!inSearch && event.key === 'End') next = optionStops[optionStops.length - 1];
    else return;
    event.preventDefault();
    next?.focus();
  };

  return (
    <ActionPopover
      exclusive={exclusive}
      open={open}
      onOpenChange={changeOpen}
      label={label}
      className="dd__pop"
      rootClassName={`dd${block ? ' dd--block' : ''} ${className}`}
      onOpenAutoFocus={(event) => {
        // With a search field it takes the focus (the default); otherwise land on the current choice.
        const panel = event.currentTarget;
        if (showSearch || !(panel instanceof HTMLElement)) return;
        const target =
          panel.querySelector<HTMLElement>('[role="option"][aria-selected="true"]') ??
          panel.querySelector<HTMLElement>('[role="option"]');
        if (!target) return;
        event.preventDefault();
        target.focus();
      }}
      trigger={
        <button
          type="button"
          className={`dd__btn${noChevron ? ' dd__btn--bare' : ''}${open ? ' is-open' : ''}`}
          aria-haspopup="listbox"
          aria-label={label}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowDown' || open) return;
            event.preventDefault();
            changeOpen(true);
          }}
        >
          <span className="dd__inner">
            {children}
            {current?.chip ? (
              <span
                className="chip"
                style={{
                  background: CHIP_COLOR[current.chip].bg,
                  color: CHIP_COLOR[current.chip].fg,
                }}
              >
                {current.label}
              </span>
            ) : (
              <span className="dd__label">{current?.label ?? '—'}</span>
            )}
            {noChevron ? null : <Chevron />}
          </span>
        </button>
      }
    >
      <div className="dd__body" onKeyDown={moveFocus}>
        <div className="dd__list" role="listbox" aria-label={label}>
          {shown.map((option) => (
            <div className="dd__rowwrap" key={option.value}>
              <button
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={option.value === value}
                className={`dd__opt${option.value === value ? ' is-selected' : ''}`}
                onClick={() => pick(option)}
              >
                <span className="dd__optlabel">
                  {option.chip ? (
                    <span
                      className="chip"
                      style={{
                        background: CHIP_COLOR[option.chip].bg,
                        color: CHIP_COLOR[option.chip].fg,
                      }}
                    >
                      {option.label}
                    </span>
                  ) : (
                    option.label
                  )}
                </span>
                {/* dd.html puts the check after the label: it sits on the right. */}
                <span className="dd__check">
                  {option.value === value && value !== '' ? '✓' : ''}
                </span>
              </button>
              {option.editable && onRename && onRemove && option.value !== '' ? (
                <button
                  type="button"
                  className="dd__edit"
                  aria-label={t('database.editOption', { name: option.label })}
                  title={t('database.duplicateEditOption')}
                  onClick={() => {
                    setEditing(editing === option.value ? null : option.value);
                    setRenameValue(option.label);
                  }}
                >
                  <Glyph d="M5 12h.01M12 12h.01M19 12h.01" size={16} strokeWidth={3} />
                </button>
              ) : null}
              {editing === option.value && onRename && onRecolor && onRemove ? (
                <OptionEditor
                  name={renameValue}
                  color={option.chip ?? 'grey'}
                  onName={setRenameValue}
                  onSave={() => {
                    onRename(option.value, renameValue.trim() || option.label);
                    setEditing(null);
                  }}
                  onRecolor={(color) => onRecolor(option.value, color)}
                  onDelete={() => {
                    onRemove(option.value);
                    setEditing(null);
                  }}
                />
              ) : null}
            </div>
          ))}
          {canCreate ? (
            <button type="button" className="dd__create" onClick={() => onCreate?.(query.trim())}>
              {t('database.createOption', { name: query.trim() })}
            </button>
          ) : null}
          {shown.length === 0 && !canCreate ? (
            <span className="dd__empty">{t('database.noOptions')}</span>
          ) : null}
        </div>
        {showSearch ? (
          <input
            ref={searchRef}
            className="ni dd__search"
            value={query}
            placeholder={t('database.searchOrCreate')}
            aria-label={t('database.searchOptions')}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              // One match left: the user is looking for that option, not for a new one.
              // Creating as well would both create "doi" and pick "Doing".
              const only = shown.length === 1 ? shown[0] : undefined;
              if (only) {
                pick(only);
                return;
              }
              if (canCreate) onCreate?.(query.trim());
            }}
          />
        ) : null}
      </div>
    </ActionPopover>
  );
}

function Chevron(): React.ReactElement {
  return <Glyph d="M6 9l6 6 6-6" size={12} strokeWidth={2} className="dd__chev" />;
}

/** Options for a select/status/multi property, with edit tools enabled. */
export function optionToDropdown(options: PropertyOption[]): DropdownOption[] {
  return options.map((option) => ({
    value: option.n,
    label: option.n,
    chip: option.c,
    editable: true,
  }));
}
